import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useTranslation } from 'react-i18next';
import { Calendar, Card, Column, ColumnFilterElementTemplateOptions, DataTable, DataTableFilterMeta, DDCountries, DDStatus, Dialog, FilterMatchMode, FloatLabel, ProgressBar, SCIcon, Tag } from '../../Common/Imports';
import { ApiResponse, Reservations_EXT } from '../../Models/Classes';
import { MailProvider } from '../Mail/Context';
import OperativeComunications from './Components/Comunications';
import OperativeContacts from './Components/Contacts';
import MailNew from '../Mail/New';
import ReservationDetail from '../Reservations/Reservation_Detail';
import { useToast } from '../../Helpers/ToastHelper';
import OperativePaxes from './Components/Paxes';
import { ToDateStringNoTime } from '../../Common/Utility/ToDateStringNoTime';
import { ExcelExport } from '../../Common/Utility/ExcelExport';

export const OperativeListMailToSend: React.FC = () => {
    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<Reservations_EXT[]>([]);
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [SelectedData, setSelectedData] = useState<Reservations_EXT | undefined>();
    const [SelectedType, setSelectedType] = useState<number | undefined>();
    const [IDReservation, SetIDReservation] = useState<number | undefined>();
    const [dateBefore, setDateBefore] = useState<Date>(() => {
        const today = new Date();
        today.setDate(today.getDate() + 1); // Adds 1 day (24 hours) to the current date
        return today;
    });
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const [TableFilters, setTableFilters] = useState<DataTableFilterMeta>({
        idReservation: { value: null, matchMode: FilterMatchMode.CONTAINS },
        sendState: { value: null, matchMode: FilterMatchMode.CONTAINS },
        idReservationOperationalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
        idReservationCustomerStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
        providerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        customerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        propertyName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        country: { value: null, matchMode: FilterMatchMode.EQUALS },
        refPax: { value: null, matchMode: FilterMatchMode.CONTAINS },
        checkInDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        checkOutDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        cancellationDate: { value: null, matchMode: FilterMatchMode.DATE_IS },
        sendBefore: { value: null, matchMode: FilterMatchMode.DATE_IS }
    });

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const localDate = [
                dateBefore.getFullYear(),
                String(dateBefore.getMonth() + 1).padStart(2, '0'),
                String(dateBefore.getDate()).padStart(2, '0')
            ].join('-');
            const resp = await AxiosHelper.get<ApiResponse<Reservations_EXT[]>>(
                `Operative/GetListWithMailToBeSend`,
                { params: { dataBefore: localDate } }
            );
            if (resp.data.code === 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    checkInDate: new Date(item.checkInDate),
                    checkOutDate: new Date(item.checkOutDate),
                    bookingDate: new Date(item.bookingDate),
                    sendBefore: new Date(item.sendBefore)
                }));
                SetTableData(processedData);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, dateBefore]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const DoubleBodyTemplate = (Name1: string, Name2: string) => {
        return (
            <>
                {Name1}
                <br />
                {Name2}
            </>
        )
    }

    const IconTemplate = (data: Reservations_EXT, type: number) => {
        switch (type) {
            case 0:
                return (
                    <SCIcon
                        Name='Mail'
                        OnClick={() => {
                            setSelectedData(data);
                            setSelectedType(0);
                        }} Pointer />);
            case 1:
                return (
                    <SCIcon
                        Classes="text-danger"
                        Name='MailForProvider'
                        OnClick={() => {
                            setSelectedData(data);
                            setSelectedType(1);
                        }} Pointer />);
            case 2:
                return (
                    <SCIcon
                        Name='Comunications'
                        OnClick={() => {
                            setSelectedData(data);
                            setSelectedType(2)
                        }} Pointer />);
            case 3:
                return (
                    <SCIcon
                        Name='Contacts'
                        OnClick={() => {
                            setSelectedData(data);
                            setSelectedType(3)
                        }} Pointer />)
        }
    }

    const UpdateRowData = async (Data: Reservations_EXT) => {
        try {
            await AxiosHelper.post(`Reservation/UpdateReservation`, Data);
            showToast('success', 'Success', t("Common.Toasts.Update.Success"));
            GetData();
        } catch (error: unknown) {
            console.log(error);
            showToast('error', 'Error', t("Common.Toasts.Update.Error"));
        }
    }

    const UpdateOperationalStatus = (RowData: Reservations_EXT, StatusId: number) => {
        RowData.idReservationOperationalStatus = StatusId;
        UpdateRowData(RowData);
    }

    const OperativeStatusTemplate = (RowData: Reservations_EXT) => {
        return <DDStatus IDType={2} value={RowData.idReservationOperationalStatus} onChange={e => {
            if (e) UpdateOperationalStatus(RowData, e);
        }
        } />;
    };

    const CustomerStatusTemplate = (RowData: Reservations_EXT) => {
        return <Tag className={RowData.customerStatus.cssClasses}>{RowData.customerStatus.value}</Tag>;
    };

    const CountryFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <DDCountries value={options.value} onChange={(e) => options.filterCallback(e, options.index)} />;
    };

    const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return <Calendar
            value={options.value}
            onChange={(e) => options.filterCallback(e.value, options.index)}
            dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999" />;
    };

    const StatusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
        return (
            <DDStatus
                IDType={2}
                value={options.value}
                onChange={(e) => {
                    if (e !== undefined && e !== null) {
                        options.filterCallback(e, options.index); // Pass the value correctly to the filter callback
                    }
                }}
            />
        );
    };

    const HeaderTemplate = (
        <div className="flex justify-content-between align-items-center pt-2 pe-2 ps-2">
            <div className="flex align-items-center">
                <SCIcon Name="Refresh" Pointer OnClick={() => GetData()} />
            </div>
            <div className="flex align-items-center">
                <SCIcon Name="Export" Pointer OnClick={() => ExcelExport(TableData, TableFilters, "EXPORT_Reservations")} />
            </div>
        </div>
    );

    const rowClass = (data: Reservations_EXT) => {
        return data.internalStatus.cssClasses;
    };

    return (
        <>
            {

                SelectedData && (SelectedType === 0 || SelectedType === 1) &&
                <Dialog visible={true} onHide={() => setSelectedData(undefined)}>
                    <MailProvider>
                        <MailNew IdReservation={SelectedData.idReservation} Type={SelectedType} PopupMode={true} />
                    </MailProvider>
                </Dialog>
            }
            {
                (SelectedData && SelectedType === 2) && <OperativeComunications
                    IDReservation={SelectedData.idReservation} OnHide={() => {
                        setSelectedData(undefined);
                    }} />
            }

            {
                (SelectedData && SelectedType === 3) && <OperativeContacts
                    IDReservation={SelectedData.idReservation}
                    IDProperty={SelectedData.idProperty}
                    ShowPropertyContacts
                    ShowReservationContacts
                    OnHide={() => {
                        setSelectedData(undefined);
                    }} />
            }
            {
                IDReservation &&
                <ReservationDetail
                    IDReservation={IDReservation}
                    OnHide={() => {
                        SetIDReservation(undefined);
                    }}
                />
            }
            {
                (SelectedData && SelectedType === 4) &&
                <OperativePaxes
                    IDReservation={SelectedData.idReservation} />
            }
            <div className="row mb-2">
                <div className="col-md-12">
                    <Card>
                        <FloatLabel>
                            <Calendar
                                id="Calendar"
                                value={dateBefore}
                                onChange={(e) => setDateBefore(e.value as Date)}
                                dateFormat="dd/mm/yy"  // PrimeReact dateFormat for dd/mm/yyyy format
                            />
                            <label htmlFor="Calendar">{t("Operative.SendBefore")}</label>
                        </FloatLabel>
                    </Card>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <Card header={HeaderTemplate}>
                        {
                            TableLoader ?
                                <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                                :
                                <DataTable
                                    value={TableData}
                                    size="small"
                                    stripedRows
                                    paginator
                                    rows={10}
                                    onFilter={(e) => setTableFilters(e.filters)}
                                    filters={TableFilters}
                                    rowsPerPageOptions={[10, 20, 50]}
                                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    rowClassName={rowClass}
                                    tableStyle={{ minWidth: '50rem' }}>
                                    <Column
                                        sortable
                                        filter
                                        header={t("Operative.Table.ResId")}
                                        field="idReservation"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        body={(e: Reservations_EXT) => (
                                            <SCIcon
                                                Pointer
                                                Name="Info"
                                                Tooltip={t("Operative.Table.OpenDetail")}
                                                OnClick={() => SetIDReservation(e.idReservation)}
                                            />
                                        )}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        body={(e: Reservations_EXT) => IconTemplate(e, 0)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        body={(e: Reservations_EXT) => IconTemplate(e, 1)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        body={(e: Reservations_EXT) => IconTemplate(e, 2)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        body={(e: Reservations_EXT) => IconTemplate(e, 3)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        filter
                                        sortable
                                        header={t("Operative.Table.OperativeStatus")}
                                        field="idReservationOperationalStatus"
                                        filterElement={StatusFilterTemplate}
                                        body={(e: Reservations_EXT) => OperativeStatusTemplate(e)}
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        filter
                                        sortable
                                        field="customerStatus.value"
                                        header={t("Operative.Table.ResCustomerStatus")}
                                        filterField="idReservationCustomerStatus"
                                        bodyClassName="table-cell-nowrap"
                                        body={(e: Reservations_EXT) => CustomerStatusTemplate(e)}
                                    />
                                    <Column
                                        filter
                                        sortable
                                        field="stakeholder.name"
                                        header={t("Operative.Table.Stakeholder")}
                                        filterField="stakeholder.name"
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        filter
                                        sortable
                                        field="brand.name"
                                        header={t("Operative.Table.Brand")}
                                        filterField="brand.name"
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        filter
                                        sortable
                                        header={t("Operative.Table.ProviderReservationCode")}
                                        field="providerReservationCode"
                                        bodyClassName="table-cell-nowrap" body={(e: Reservations_EXT) => { return <a className="underline text-primary" style={{ cursor: "pointer" }} onClick={() => SetIDReservation(e.idReservation)}>{e.providerReservationCode}</a> }}
                                    />
                                    <Column
                                        filter
                                        sortable
                                        header={t("Operative.Table.CustomerReservationCode")}
                                        field="customerReservationCode"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        sortable
                                        filter
                                        field="propertyName"
                                        header={t("Operative.Table.HotelName")}
                                        body={(e: Reservations_EXT) => DoubleBodyTemplate(e.propertyName, e.property?.name)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        sortable
                                        field="country"
                                        filterElement={CountryFilterTemplate}
                                        header={t("Operative.Table.Country")}
                                        body={(e: Reservations_EXT) => DoubleBodyTemplate(e.propertyCountry, e.property?.country)}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        sortable
                                        filter
                                        field="refPax"
                                        header={t("Operative.Table.RefPax")}
                                        body={(e: Reservations_EXT) => {
                                            return <a className="underline text-primary" style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    setSelectedData(e);
                                                    setSelectedType(4);
                                                }}>{e.refPax}</a>
                                        }}
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        sortable
                                        filter
                                        dataType="date"
                                        header={t("Operative.Table.CheckIn")}
                                        body={(e: Reservations_EXT) => ToDateStringNoTime(e.checkInDate)}
                                        filterElement={dateFilterTemplate}
                                        filterField="checkInDate"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        sortable
                                        filter
                                        dataType="date"
                                        header={t("Operative.Table.CheckOut")}
                                        body={(e: Reservations_EXT) => ToDateStringNoTime(e.checkOutDate)}
                                        filterElement={dateFilterTemplate}
                                        filterField="checkOutDate"
                                        bodyClassName="table-cell-nowrap" />
                                    <Column
                                        filter
                                        sortable
                                        dataType="date"
                                        header={t("Operative.Table.CancellationDate")}
                                        body={(e: Reservations_EXT) => e.cancellationDate?.toLocaleDateString()}
                                        filterElement={dateFilterTemplate}
                                        filterField="cancellationDate"
                                        sortField="cancellationDate"
                                        bodyClassName="table-cell-nowrap"
                                    />
                                    <Column
                                        sortable
                                        filter
                                        dataType="date"
                                        header={t("Operative.Table.SendBefore")}
                                        body={(e: Reservations_EXT) => e.sendBefore.toLocaleDateString()}
                                        filterElement={dateFilterTemplate}
                                        filterField="sendBefore"
                                        bodyClassName="table-cell-nowrap" />
                                </DataTable>
                        }
                    </Card>
                </div>
            </div>
        </>
    );
};

export default OperativeListMailToSend;
